import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Clock, Package, Users } from 'lucide-react';
import { MonsterTargetRow } from './MonsterTargetRow';
import { EventChip } from './EventChip';
import { itemName, itemPath, mapPath, mapName } from '../../utils/worldFormat';
import { routeKindLabel } from '../../utils/worldQueries';
import { qualityLabel, routeQuality } from '../../utils/itemSources';
import type { ResolvedSource } from '../../utils/itemSources';
import { DataStatus } from './DataStatus';
import type { WorldData } from '../../types/world';
const PREVIEW = 6;
function Targets({
  w,
  s
}: {
  w: WorldData;
  s: ResolvedSource;
}) {
  const [all, setAll] = useState(false);
  if (!s.targets.length) return null;
  const shown = all ? s.targets : s.targets.slice(0, PREVIEW);
  const viaNames = s.route.viaItemIds.map(id => itemName(w, id));
  return <div className="mt-3" data-id="element-1787" data-mp="qndbi5z4yzvo">
      <p className="mb-2 text-xs font-semibold text-gray-400" data-id="element-1788" data-mp="qnwln0ijojz5">Monstruos que pueden soltarlo · {s.targets.length}</p>
      <ul className="grid gap-2" data-id="element-1789" data-mp="1pbmaf5qqlxtu">{shown.map(t => <MonsterTargetRow key={`${t.monsterId}-${t.mapId ?? ''}-${t.eventId ?? ''}-${t.isIndirect ? 1 : 0}`} w={w} t={t} viaNames={viaNames} data-id="element-1790" data-mp="1pf0b89jr5yz6" />)}</ul>
      {s.targets.length > PREVIEW && <button type="button" onClick={() => setAll(a => !a)} className="mt-2 h-10 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]" data-id="element-1791" data-mp="1z8qxd7sepxbp">
          {all ? 'Mostrar menos' : `Ver los ${s.targets.length} monstruos`}
        </button>}
    </div>;
}

/** Fuente de obtención para jugadores: sin nombres de archivo ni IDs técnicos. */
export function SourceCard({
  w,
  s,
  nested = false
}: {
  w: WorldData;
  s: ResolvedSource;
  nested?: boolean;
}) {
  const disabled = s.status === 'disabled';
  return <li className={`rounded-xl md:rounded-2xl border p-3 md:p-4 text-[13px] md:text-base ${disabled ? 'border-white/5 bg-white/[0.01] opacity-80' : nested ? 'border-white/10 bg-[#050b14]/40' : 'border-white/10 bg-[#0A1628]'}`} data-id="element-1792" data-mp="1lcvkbikovgg8">
      <div className="flex flex-wrap items-center gap-2" data-id="element-1793" data-mp="ktjz698prbn9">
        <span className="rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[11px] font-semibold text-[#F3E5AB]" data-id="element-1794" data-mp="krz48iihlqov">{s.label ?? routeKindLabel[s.kind] ?? s.kind}</span>
        <span className="font-semibold text-white" data-id="element-1795" data-mp="uitqvnznasau">{s.title}</span>
        {routeQuality(s.route) !== 'normal' && <span className="rounded-full border border-sky-300/30 px-2 py-0.5 text-[11px] font-semibold text-sky-100" data-id="element-1796" data-mp="plae6silinl0">{qualityLabel[routeQuality(s.route)]}{routeQuality(s.route) === 'other' && s.route.quality ? `: ${s.route.quality}` : ''}</span>}
        {!disabled && !nested && <span className="ml-auto" data-id="element-1797" data-mp="bu5anfc3xb5e">
            {s.pending ? <DataStatus kind="partial" data-id="element-1798" data-mp="6trccp0b5fkk" /> : s.eventMaps.length || s.eventMapId !== undefined || s.targets.some(t => t.spawns.length) ? <DataStatus kind="location" withDate={false} data-id="element-1799" data-mp="1n6pn6rlayi73" /> : <DataStatus kind="route" withDate={false} data-id="element-1800" data-mp="1i8o446nvmivm" />}
          </span>}
        {s.route.variantId && s.route.variantId !== s.route.itemId && w.variants.has(s.route.variantId) && <Link to={itemPath(s.route.variantId)} className="rounded-full border border-white/15 px-2 py-0.5 text-[11px] font-semibold text-gray-200 hover:border-[#D4AF37]/50" data-id="element-1801" data-mp="1utmqhl3615be">
            Variante: {itemName(w, s.route.variantId)}
          </Link>}
      </div>

      {disabled && s.playerStatusNote && <p className="mt-2 flex items-start gap-1.5 text-sm text-red-200" data-id="element-1802" data-mp="28k8876gvv97p">
          <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1803" data-mp="2dh878xfbfbpb" />
          {s.playerStatusNote}
        </p>}
      {s.status === 'partial' && <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-100" data-id="element-1804" data-mp="23shxv0hy4lga">
          <Users className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1805" data-mp="23qdhvk6rvvzn" />
          {s.playerProbability}
        </p>}
      {s.conditions && <p className="mt-1.5 text-sm text-gray-300" data-id="element-1806" data-mp="2w9ap2c3p0y15">{s.conditions}</p>}
      {s.status !== 'partial' && s.playerProbability && <p className="mt-1 text-sm text-gray-300" data-id="element-1807" data-mp="18likwv2i65kz">{s.playerProbability}</p>}

      {s.eventMapId !== undefined && <p className="mt-2 text-sm text-gray-400" data-id="element-1808" data-mp="13kbd4w0d16eb">
          Mapa: <Link to={mapPath(s.eventMapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1809" data-mp="ylrqsuhutslf">{mapName(w, s.eventMapId)}</Link>
        </p>}

      {s.eventMaps.length > 0 && <div className="mt-3" data-id="element-1810" data-mp="1r9vzlovq2lsc">
          <p className="mb-2 text-xs font-semibold text-gray-400" data-id="element-1811" data-mp="1rdbcqm5u5kr4">Premio del evento en</p>
          <ul className="flex flex-wrap gap-2" data-id="element-1812" data-mp="1mcw0xz17clb9">
            {s.eventMaps.map(m => <li key={`${m.mapId}-${m.eventId ?? ''}`} className="flex items-center gap-2" data-id="element-1813" data-mp="1hjuijh1se3mx">
                <Link to={mapPath(m.mapId)} className="inline-flex h-9 items-center rounded-full border border-white/15 px-3 text-xs font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-1814" data-mp="1djaowngf9i5j">
                  {mapName(w, m.mapId)}
                </Link>
                {m.eventId && <EventChip w={w} eventId={m.eventId} data-id="element-1815" data-mp="3nyrr8s84m5gh" />}
              </li>)}
          </ul>
        </div>}

      <Targets w={w} s={s} data-id="element-1816" data-mp="3sx31v568wjzr" />

      {!disabled && (s.targets.length > 0 || s.eventMaps.length > 0) && <p className="mt-3 text-xs text-gray-500" data-id="element-1817" data-mp="rpgts2v0mgh">
          Combinación posible según la configuración del servidor: no es un drop garantizado
          {s.runtimeTested === true ? '.' : ' y no está comprobada dentro del juego.'}
        </p>}

      {s.via && s.via.length > 0 && <div className="mt-3 space-y-3 border-l-2 border-[#D4AF37]/30 pl-3" data-id="element-1818" data-mp="fogfu08mfw5k">
          {s.via.map(v => {
        const shown = v.sources.filter(c => c.status !== 'disabled' && !c.pending);
        return <div key={v.itemId} data-id="element-1819" data-mp="okfovq7n6llh">
                <p className="flex flex-wrap items-center gap-1.5 text-sm text-gray-300" data-id="element-1820" data-mp="tih605licaub">
                  <Package className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1821" data-mp="yg98ftjs3fcr" />
                  Primero consigue
                  <Link to={itemPath(v.itemId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1822" data-mp="2ukyu0xpajck4">{v.name}</Link>
                </p>
                {shown.length > 0 && <ul className="mt-2 grid gap-2" data-id="element-1823" data-mp="34a07oh8d5hux">{shown.map(c => <SourceCard key={c.key} w={w} s={c} nested data-id="element-1824" data-mp="34btim8q0eo8q" />)}</ul>}
              </div>;
      })}
        </div>}

      {s.pending && !disabled && <p className="mt-3 flex items-center gap-2 rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm text-gray-300" data-id="element-1825" data-mp="3d93qdk3mbv8w">
          <Clock className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1826" data-mp="398jx4rm61bs1" />
          Ubicación de obtención pendiente de confirmar
        </p>}
    </li>;
}